<template>
  <div class="product-selector">
    <!-- Search Section -->
    <div class="mb-3">
      <label
        class="form-label text-xs text-uppercase font-weight-bold text-muted"
        >Search Products</label
      >
      <div class="input-group">
        <span class="input-group-text bg-white border-end-0">
          <i class="fas fa-search text-muted"></i>
        </span>
        <input
          v-model="searchQuery"
          type="text"
          class="form-control border-start-0 ps-0"
          placeholder="Search by name..."
          @input="handleSearch"
        />
      </div>
    </div>

    <!-- Search Results -->
    <div v-if="searchResults.length > 0" class="mb-4 fade-in">
      <h6 class="text-xs font-weight-bold text-uppercase text-muted mb-2">
        Results
      </h6>
      <div
        class="list-group shadow-sm"
        style="max-height: 250px; overflow-y: auto"
      >
        <button
          v-for="p in searchResults"
          :key="p.id"
          class="list-group-item list-group-item-action d-flex justify-content-between align-items-center p-3 border-start-0 border-end-0"
          @click="addProduct(p)"
        >
          <div class="d-flex align-items-center gap-3">
            <div
              class="avatar avatar-md rounded bg-light d-flex align-items-center justify-content-center"
            >
              <img
                v-if="p.image_url"
                :src="p.image_url"
                class="w-100 h-100 object-cover rounded"
              />
              <i v-else class="fas fa-image text-muted text-lg"></i>
            </div>

            <div class="text-start">
              <h6 class="mb-0 text-sm font-weight-bold text-dark">
                {{ p.name }}
              </h6>
              <span class="text-xs text-muted">{{ p.product_code }}</span>
            </div>
          </div>
          <i
            class="fas fa-plus-circle text-primary text-lg transition-transform hover-scale"
          ></i>
        </button>
      </div>
    </div>

    <div
      v-if="searchQuery && searchResults.length === 0 && !isLoading"
      class="text-center py-3 text-muted text-sm"
    >
      No products found.
    </div>

    <!-- Selected Products -->
    <div class="selected-products-section mt-4 pt-3 border-top">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="text-xs font-weight-bold text-uppercase text-muted mb-0">
          Selected Products
          <span class="badge bg-dark rounded-pill ms-2">{{
            modelValue.length
          }}</span>
        </h6>
        <span
          v-if="modelValue.length > 1"
          class="text-xxs text-muted fst-italic"
          >Drag or use arrow keys to reorder (Bundle priority)</span
        >
      </div>

      <div
        v-if="selectedProducts.length === 0"
        class="text-center py-5 bg-light rounded border border-dashed text-muted"
      >
        <i class="fas fa-box-open text-3xl mb-3 opacity-5"></i>
        <p class="mb-0 text-sm">No products selected yet.</p>
      </div>

      <div v-else class="d-flex flex-column gap-2">
        <div
          v-for="(p, idx) in orderedSelectedProducts"
          :key="p.id"
          class="d-flex align-items-center bg-white border rounded p-2 shadow-sm transition-all hover-shadow"
        >
          <!-- Order Controls -->
          <div class="d-flex flex-column gap-1 me-3 px-1 text-muted">
            <i
              class="fas fa-chevron-up cursor-pointer hover-text-dark text-xs"
              @click.prevent="moveUp(idx)"
              :class="{ 'opacity-0 pointer-events-none': idx === 0 }"
            ></i>
            <span class="text-xs font-weight-bold text-center">{{
              idx + 1
            }}</span>
            <i
              class="fas fa-chevron-down cursor-pointer hover-text-dark text-xs"
              @click.prevent="moveDown(idx)"
              :class="{
                'opacity-0 pointer-events-none':
                  idx === orderedSelectedProducts.length - 1,
              }"
            ></i>
          </div>

          <!-- Product Info -->
          <div class="d-flex align-items-center gap-3 flex-grow-1">
            <div class="avatar avatar-sm rounded bg-light border">
              <img
                v-if="p.image_url"
                :src="p.image_url"
                class="w-100 h-100 object-cover rounded"
              />
            </div>
            <div>
              <h6 class="mb-0 text-sm text-dark">{{ p.name }}</h6>
              <span class="text-xs text-muted">Code: {{ p.product_code }}</span>
            </div>
          </div>

          <!-- Actions -->
          <button
            class="btn btn-link text-danger mb-0 px-2"
            @click="removeProduct(p.id)"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import { debounce } from "lodash"; // Assuming lodash is available or I should write simple debounce

export default {
  name: "ProductSelector",
  props: {
    modelValue: {
      type: Array, // Array of IDs
      default: () => [],
    },
    limit: {
      type: Number,
      default: 0, // 0 = unlimited
    },
  },
  data() {
    return {
      searchQuery: "",
      searchResults: [],
      selectedProductsFull: [], // Cache for selected product details
      isLoading: false,
    };
  },
  computed: {
    selectedProducts() {
      // Return details for IDs in modelValue, ensuring order matches modelValue
      if (!this.modelValue) return [];
      return this.modelValue
        .map((id) => this.selectedProductsFull.find((p) => p.id === id))
        .filter(Boolean);
    },
    orderedSelectedProducts() {
      return this.selectedProducts;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.fetchSelectedDetails(newVal);
        } else {
          this.selectedProductsFull = [];
        }
      },
    },
  },
  methods: {
    handleSearch: debounce(function () {
      this.searchProducts();
    }, 300),

    async searchProducts() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }
      this.isLoading = true;
      try {
        const res = await request.get("/api/shop/products", {
          params: { search_name: this.searchQuery },
        });
        // Filter out already selected
        this.searchResults = res.data.data.filter(
          (p) => !this.modelValue.includes(p.id)
        );
      } catch (e) {
        console.error("Search failed", e);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSelectedDetails(ids) {
      // Find which IDs we don't have details for yet
      const missingIds = ids.filter(
        (id) => !this.selectedProductsFull.find((p) => p.id === id)
      );

      if (missingIds.length === 0) return;

      try {
        const res = await request.get("/api/shop/products", {
          params: {
            ids: missingIds.join(","),
            no_pagination: 1,
          },
        });
        const newDetails = res.data.data;
        this.selectedProductsFull = [
          ...this.selectedProductsFull,
          ...newDetails,
        ];
      } catch (e) {
        console.error("Fetch details failed", e);
      }
    },

    addProduct(product) {
      if (this.limit > 0 && this.modelValue.length >= this.limit) {
        alert(`Limit reached (Max ${this.limit})`);
        return;
      }
      // Add to modelValue
      const newIds = [...this.modelValue, product.id];
      this.$emit("update:modelValue", newIds);

      // Add to details cache
      if (!this.selectedProductsFull.find((p) => p.id === product.id)) {
        this.selectedProductsFull.push(product);
      }

      // Clear search/results
      this.searchQuery = "";
      this.searchResults = [];
    },

    removeProduct(id) {
      const newIds = this.modelValue.filter((vid) => vid !== id);
      this.$emit("update:modelValue", newIds);
    },

    moveUp(index) {
      if (index === 0) return;
      const newIds = [...this.modelValue];
      [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
      this.$emit("update:modelValue", newIds);
    },

    moveDown(index) {
      if (index === this.modelValue.length - 1) return;
      const newIds = [...this.modelValue];
      [newIds[index + 1], newIds[index]] = [newIds[index], newIds[index + 1]];
      this.$emit("update:modelValue", newIds);
    },
  },
};
</script>
